import React, { useState, useEffect } from "react";
import { Formik, Form, FormikHelpers } from "formik";

import { Upload, createUpload } from "../../../models/Upload";
import {
  Step1,
  StepTitle1,
  StepValidation1,
  StepInstructions1,
} from "./ui/Step1";
import {
  Step2,
  StepTitle2,
  StepValidation2,
  StepInstructions2,
} from "./ui/Step2";
import {
  Step3,
  StepTitle3,
  StepValidation3,
  StepInstructions3,
} from "./ui/Step3";
import {
  Step4,
  StepTitle4,
  StepValidation4,
  StepInstructions4,
} from "./ui/Step4";
import {
  Step5,
  StepTitle5,
  StepValidation5,
  StepInstructions5,
} from "./ui/Step5";
import { UploadFormStyles } from "./UploadForm.style";
import { useTranslation } from "react-i18next";
import {
  UploadStepper,
  UploadStep,
} from "./../../controls/UploadStepper/UploadStepper";
import { useGetTargets } from "../../../hooks/useGetTargets/useGetTargets";
import { Target } from "../../../models/Target";
import { useGetLimit } from "../../../hooks/useGetLimit/useGetLimit";
import { createLimit } from "../../../models/Limit";
import { useGetGeneral } from "../../../hooks/useGetGeneral/useGetGeneral";
import { createGeneral } from "../../../models/General";

interface UploadFormProps {
  submit: (upload: Upload) => void;
}

export const UploadForm: React.FC<UploadFormProps> = ({ submit }) => {
  const [step, setStep] = useState(0);
  const [initialValues] = useState(createUpload());
  const [targets, setTargets] = useState<Target[]>([]);
  const [limit, setLimit] = useState(createLimit());
  const [general, setGeneral] = useState(createGeneral());
  const { t } = useTranslation();
  const getTargets = useGetTargets();
  const getLimit = useGetLimit();
  const getGeneral = useGetGeneral();
  const styles = UploadFormStyles({});

  const steps: UploadStep[] = [
    { label: StepTitle1(t), text: StepInstructions1(t) },
    { label: StepTitle2(t), text: StepInstructions2(t) },
    { label: StepTitle3(t), text: StepInstructions3(t) },
    { label: StepTitle4(t), text: StepInstructions4(t) },
    { label: StepTitle5(t), text: StepInstructions5(t) },
  ];
  const contents = [
    <Step1 />,
    <Step2 targets={targets} codeLabel={general.codeLabel} />,
    <Step3 limit={limit} requireNonDicomAnnotation={general.requireNonDicomAnnotation}/>,
    <Step4 otherPatientId={general.otherPatientId} />,
    <Step5 termsOfUse={general.termsOfUse} />,
  ];
  const validations = [
    StepValidation1(t),
    StepValidation2(t),
    StepValidation3(t),
    StepValidation4(t),
    StepValidation5(t),
  ];

  const postProcessUpload = (upload: Upload) => {
    // other patient id mode 2 (write uploader emails into other patient id)
    if (general.otherPatientId === 2 && upload.patient)
      upload.patient.otherid = upload.user.email;
  }

  const isLastStep = () => {
    return step === steps.length - 1;
  };

  const handleNext = () => {
    setStep((prevStep) => Math.min(prevStep + 1, steps.length - 1));
  };

  const handleBack = () => {
    setStep((prevStep) => Math.max(prevStep - 1, 0));
  };

  const handleSubmit = (values: Upload, helpers: FormikHelpers<Upload>) => {
    const { setSubmitting, setTouched } = helpers;

    if (!isLastStep()) {
      setTouched({});
      setSubmitting(false);
      handleNext();
      return;
    }

    // post-process upload data
    postProcessUpload(values);

    setSubmitting(true);
    submit(values);
  };

  // handle targets request
  useEffect(() => {
    if (getTargets.loading) return;
    if (getTargets.data) setTargets(getTargets.data);
    if (getTargets.error) console.error(getTargets.error);
  }, [getTargets]);

  // handle limit request
  useEffect(() => {
    if (getLimit.loading) return;
    if (getLimit.data) setLimit(getLimit.data);
    if (getLimit.error) console.error(getLimit.error);
  }, [getLimit]);

  // handle config request
  useEffect(() => {
    if (getGeneral.loading) return;
    if (getGeneral.data) setGeneral(getGeneral.data);
    if (getGeneral.error) console.error(getGeneral.error);
  }, [getGeneral]);

  return (
    <div className={styles.root}>
      <Formik<Upload>
        validateOnChange={true}
        validateOnBlur={true}
        validateOnMount={true}
        initialValues={initialValues}
        validationSchema={validations[step]}
        onSubmit={handleSubmit}
      >
        <>
          <Form>
            <UploadStepper activeStep={step} steps={steps} onBack={handleBack}>
              <div>{contents[step]}</div>
            </UploadStepper>
          </Form>
        </>
      </Formik>
    </div>
  );
};
